import React, { useEffect, useRef, useState } from "react";
import {
  Typography,
  IconButton,
  Box,
  useTheme,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
  useMediaQuery,
  Fade,
  Paper,
  Tooltip,
  Zoom,
  Stack,
  Fab,
  Badge,
} from "@mui/material";
import {
  DeleteTwoTone,
  DragIndicator,
  ContentCopyOutlined,
  PlaylistAddOutlined,
} from "@mui/icons-material";
import DraggableTaskButton from "../Buttons/DraggableTaskButton";
import validateTasks from "../../Validation/TaskValidation";
import AddTaskButton from "../Buttons/AddTaskButton";
import PropTypes from "prop-types";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import AddIcon from "@mui/icons-material/Add";
import { toast } from "react-toastify";

export default function TaskTable({ tasks, setTasks, isAuthor, onAdd }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const scrollRef = useRef(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState(null);
  const [newTaskAdded, setNewTaskAdded] = useState(false);
  const [showAddTaskDialog, setShowAddTaskDialog] = useState(false);
  const [insertPosition, setInsertPosition] = useState(-1); // -1 means append to end
  const [isPositionedInsertion, setIsPositionedInsertion] = useState(false); // Track if we're doing positioned insertion

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 3,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 0,
        tolerance: 0,
      },
    })
  );

  // Fix iPad bug: Add safety check for e.target.closest
  useEffect(() => {
    const handleTouchStart = (e) => {
      // Safe check for closest method to prevent iPad errors
      if (
        e.target &&
        typeof e.target.closest === "function" &&
        e.target.closest(".drag-handle")
      ) {
        e.target.setAttribute("data-dragging", "true");
        e.preventDefault();
      }
    };

    document.addEventListener("touchstart", handleTouchStart, {
      passive: false,
    });
    return () => {
      document.removeEventListener("touchstart", handleTouchStart);
    };
  }, []);

  const validateAndSetTasks = (newTasks) => {
    try {
      if (!Array.isArray(newTasks)) {
        console.error("validateTasks received non-array:", newTasks);
        return;
      }
      const validatedTasks = validateTasks(newTasks);
      setTasks(validatedTasks);
    } catch (error) {
      console.error("Task validation error:", error);
    }
  };

  const handleDragStart = (event) => {
    // Add haptic feedback for touch devices
    if (window.navigator.vibrate) {
      window.navigator.vibrate(50); // Short vibration for drag start
    }

    // Add a subtle animation to show the item is being dragged
    const element = document.getElementById(`task-${event.active.id}`);
    if (element) {
      element.style.transform = "scale(1.03)";
      element.style.boxShadow = theme.shadows[8];
      element.style.zIndex = "100";
    }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;

    // Add haptic feedback for touch devices on successful drop
    if (over && active.id !== over.id && window.navigator.vibrate) {
      window.navigator.vibrate([30, 30, 30]); // Pattern vibration for successful drop
    }

    // Reset animations
    const element = document.getElementById(`task-${active.id}`);
    if (element) {
      element.style.transform = "";
      element.style.boxShadow = "";
      element.style.zIndex = "";
    }

    if (!over || active.id === over.id) return;

    if (!Array.isArray(tasks)) {
      console.error("handleDragEnd: tasks is not an array", tasks);
      return;
    }

    const oldIndex = tasks.findIndex((item) => item.id === active.id);
    const newIndex = tasks.findIndex((item) => item.id === over.id);

    if (oldIndex === -1 || newIndex === -1) return;

    const newItems = arrayMove(tasks, oldIndex, newIndex);
    validateAndSetTasks(newItems);
  };

  const handleDelete = (taskId) => {
    setTaskToDelete(taskId);
    setOpenDeleteDialog(true);
  };

  const confirmDelete = () => {
    if (!Array.isArray(tasks)) {
      console.error("confirmDelete: tasks is not an array", tasks);
      return;
    }

    const updatedTasks = tasks.filter((task) => task.id !== taskToDelete);
    validateAndSetTasks(updatedTasks);
    setOpenDeleteDialog(false);
    setTaskToDelete(null);
  };

  // New function to handle task copying
  const handleCopyTask = (taskId) => {
    if (!Array.isArray(tasks)) {
      console.error("handleCopyTask: tasks is not an array", tasks);
      return;
    }

    const taskIndex = tasks.findIndex((task) => task.id === taskId);
    if (taskIndex === -1) return;

    const originalTask = tasks[taskIndex];
    const copiedTask = {
      ...originalTask,
      id: Date.now() + Math.random(), // Generate unique ID
    };

    // Insert the copied task right after the original task
    const updatedTasks = [
      ...tasks.slice(0, taskIndex + 1),
      copiedTask,
      ...tasks.slice(taskIndex + 1),
    ];

    validateAndSetTasks(updatedTasks);
    setNewTaskAdded(true);

    // Add haptic feedback
    if (window.navigator.vibrate) {
      window.navigator.vibrate(50);
    }

    // Show success toast
    toast.success("Task copied successfully", {
      autoClose: 1500,
      position: "bottom-center",
      hideProgressBar: true,
    });
  };

  // New function to handle adding task below
  const handleAddTaskBelow = (taskId) => {
    if (!Array.isArray(tasks)) {
      console.error("handleAddTaskBelow: tasks is not an array", tasks);
      return;
    }

    const taskIndex = tasks.findIndex((task) => task.id === taskId);
    console.log(
      "🔽 CLICK: Task ID:",
      taskId,
      "found at index:",
      taskIndex,
      "Total tasks:",
      tasks.length
    );

    if (taskIndex === -1) return;

    // Set insertion position to be RIGHT AFTER the current task
    const newInsertPosition = taskIndex + 1;
    console.log(
      "🔽 SETTING: Will insert new task at position:",
      newInsertPosition,
      "(after task at index",
      taskIndex,
      ")"
    );

    setInsertPosition(newInsertPosition);
    setIsPositionedInsertion(true); // Mark this as a positioned insertion
    setShowAddTaskDialog(true);

    // Add haptic feedback
    if (window.navigator.vibrate) {
      window.navigator.vibrate(50);
    }
  };

  // Function for positioned task insertion (task row button)
  const handlePositionedTaskAdd = (task) => {
    console.log(
      "🎯 INSERTING: Task at position",
      insertPosition,
      "out of total",
      tasks.length,
      "tasks"
    );
    console.log("🎯 BEFORE INSERT: Task list length =", tasks.length);

    // Store the position before any potential resets
    const targetPosition = insertPosition;
    console.log("🔒 LOCKED POSITION:", targetPosition, "for insertion");

    // Safety check: if position is -1, something went wrong
    if (targetPosition === -1) {
      console.error(
        "❌ ERROR: Position is -1 during positioned insertion! Falling back to append to end."
      );
      // Fallback to append mode
      if (onAdd) {
        onAdd(task);
      }
      // Reset state
      setInsertPosition(-1);
      setIsPositionedInsertion(false);
      return;
    }

    const taskId = Date.now() + Math.random(); // Generate unique ID
    const newTask = {
      ...task,
      id: taskId,
    };

    // Insert at the exact stored position
    const beforeTasks = tasks.slice(0, targetPosition);
    const afterTasks = tasks.slice(targetPosition);
    const updatedTasks = [...beforeTasks, newTask, ...afterTasks];

    console.log("🎯 AFTER INSERT: Task list length =", updatedTasks.length);
    console.log(
      "🎯 POSITION CHECK: New task should be at index",
      targetPosition
    );
    console.log(
      "🎯 NEW TASK:",
      newTask.name || newTask.type,
      "inserted at position",
      targetPosition
    );

    validateAndSetTasks(updatedTasks);
    setNewTaskAdded(true);

    // Reset insertion position and flag ONLY after successful insertion
    const positionForToast = targetPosition + 1; // For user-friendly 1-based numbering
    setInsertPosition(-1);
    setIsPositionedInsertion(false);

    // Success feedback
    if (window.navigator.vibrate) {
      window.navigator.vibrate(50);
    }

    // Show success toast for positioned insertion
    toast.success(`Task added at position ${positionForToast}`, {
      autoClose: 1500,
      position: "bottom-center",
      hideProgressBar: true,
    });
  };

  // Function for appending to end (floating button)
  const handleAppendTaskAdd = (task) => {
    console.log(
      "📋 APPEND: Adding task to end of list, current length:",
      tasks.length
    );

    // Reset any positioned insertion state
    setInsertPosition(-1);
    setIsPositionedInsertion(false);

    // Use parent's logic (append to end)
    if (onAdd) {
      onAdd(task);
    }
  };

  useEffect(() => {
    if (scrollRef.current && newTaskAdded) {
      const scrollContainer = scrollRef.current;
      scrollContainer.scrollTo({
        top: scrollContainer.scrollHeight,
        behavior: "smooth",
      });
      setNewTaskAdded(false);
    }
  }, [tasks, newTaskAdded]);

  const tasksArray = Array.isArray(tasks) ? tasks : [];

  return (
    <Paper
      elevation={3}
      sx={{
        p: 3,
        borderRadius: "16px",
        height: "100%",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        position: "relative",
        background:
          theme.palette.mode === "dark"
            ? `linear-gradient(145deg, ${theme.palette.background.paper}, ${theme.palette.grey[900]})`
            : `linear-gradient(145deg, ${theme.palette.background.paper}, ${theme.palette.grey[100]})`,
        transition: "all 0.3s ease",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          mb: 2,
          alignItems: "center",
        }}
      >
        <Typography variant="h6" fontWeight="600" color="primary">
          Recipe Tasks
          <Typography
            component="span"
            variant="body2"
            sx={{ ml: 1, color: theme.palette.text.secondary }}
          >
            ({tasksArray.length})
          </Typography>
        </Typography>
        {isAuthor && (
          <Tooltip
            title={
              isMobile
                ? "Touch and hold to drag items"
                : "Drag tasks to reorder"
            }
            placement="top"
            arrow
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mr: 1 }}
              >
                {isMobile ? "Touch and hold to drag" : "Drag to reorder"}
              </Typography>
              <DragIndicator fontSize="small" color="action" />
            </Box>
          </Tooltip>
        )}
      </Box>

      {tasksArray.length > 0 && (
        <Box
          sx={{
            mb: 2,
            p: 1,
            borderRadius: 1,
            backgroundColor:
              theme.palette.mode === "dark"
                ? "rgba(0,0,0,0.1)"
                : "rgba(0,0,0,0.03)",
            border: `1px dashed ${theme.palette.divider}`,
          }}
        >
          <Typography variant="body2" color="text.secondary" align="center">
            {isMobile
              ? "Touch and hold a recipe step to drag it"
              : "Click and drag tasks to reorder them"}
          </Typography>
        </Box>
      )}

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={tasksArray.map((task) => task.id)}
          strategy={verticalListSortingStrategy}
        >
          <Box
            ref={scrollRef}
            sx={{
              overflowY: "auto",
              flex: 1,
              pr: 1,
              "&::-webkit-scrollbar": { width: 6 },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? "rgba(255,255,255,0.2)"
                    : "rgba(0,0,0,0.2)",
                borderRadius: 3,
              },
              "&::-webkit-scrollbar-track": {
                backgroundColor: "transparent",
              },
            }}
          >
            {tasksArray.length === 0 ? (
              <Fade in={true} timeout={800}>
                <Box
                  sx={{
                    textAlign: "center",
                    p: 4,
                    color: theme.palette.text.secondary,
                    border: `1px dashed ${theme.palette.divider}`,
                    borderRadius: 2,
                  }}
                >
                  <Typography variant="body1">No tasks added yet</Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Tap the + button to add a task
                  </Typography>
                </Box>
              </Fade>
            ) : (
              tasksArray.map((task) => (
                <Zoom
                  in={true}
                  key={task.id}
                  style={{ transitionDelay: "50ms" }}
                >
                  <Paper
                    elevation={0}
                    sx={{
                      p: 0.5,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      width: "100%",
                      background: "transparent",
                      borderRadius: "8px",
                      transition: "all 0.2s ease",
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      sx={{ width: "100%" }}
                    >
                      <DraggableTaskButton
                        task={task}
                        onUpdate={(t) => {
                          validateAndSetTasks(
                            tasksArray.map((s) => (s.id === t.id ? t : s))
                          );
                        }}
                        isAuthor={isAuthor}
                      />
                    </Stack>
                    {isAuthor && (
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Tooltip title="Copy task" arrow>
                          <IconButton
                            onClick={() => handleCopyTask(task.id)}
                            size="small"
                            sx={{
                              color: theme.palette.info.main,
                              opacity: 0.7,
                              padding: "8px",
                              "&:hover": {
                                opacity: 1,
                                backgroundColor: theme.palette.info.light,
                                color: theme.palette.info.dark,
                              },
                              transition: "all 0.2s ease",
                            }}
                          >
                            <ContentCopyOutlined fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Add new task below" arrow>
                          <IconButton
                            onClick={() => handleAddTaskBelow(task.id)}
                            size="small"
                            sx={{
                              color: theme.palette.success.main,
                              opacity: 0.7,
                              padding: "8px",
                              "&:hover": {
                                opacity: 1,
                                backgroundColor: theme.palette.success.light,
                                color: theme.palette.success.dark,
                              },
                              transition: "all 0.2s ease",
                            }}
                          >
                            <PlaylistAddOutlined fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Delete task" arrow>
                          <IconButton
                            onClick={() => handleDelete(task.id)}
                            size="small"
                            sx={{
                              color: theme.palette.error.main,
                              opacity: 0.7,
                              padding: "8px",
                              "&:hover": {
                                opacity: 1,
                                backgroundColor: theme.palette.error.light,
                                color: theme.palette.error.dark,
                              },
                              transition: "all 0.2s ease",
                            }}
                          >
                            <DeleteTwoTone fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    )}
                  </Paper>
                </Zoom>
              ))
            )}
          </Box>
        </SortableContext>
      </DndContext>

      {/* AddTaskButton for positioned insertion (task row) */}
      <AddTaskButton
        onAdd={handlePositionedTaskAdd}
        open={showAddTaskDialog && isPositionedInsertion}
        onClose={() => {
          console.log(
            "🔄 CLOSING positioned modal - preserving position during insertion"
          );
          setShowAddTaskDialog(false);
          // Don't reset insertPosition and isPositionedInsertion here - let handlePositionedTaskAdd do it
          // This prevents race condition where position gets reset before insertion
        }}
        sx={{ display: "none" }}
      />

      {/* AddTaskButton for floating button (append to end) */}
      <AddTaskButton
        onAdd={handleAppendTaskAdd}
        open={showAddTaskDialog && !isPositionedInsertion}
        onClose={() => {
          console.log("🔄 CLOSING floating modal");
          setShowAddTaskDialog(false);
          setInsertPosition(-1);
          setIsPositionedInsertion(false);
        }}
        sx={{ display: "none" }}
      />

      {/* Floating Add Task Button - Move to top of content */}
      <Tooltip title="Add new task" placement="left">
        <Fab
          color="primary"
          aria-label="add task"
          onClick={() => {
            console.log("🌟 FLOATING BUTTON: Clicked - setting append mode");
            setInsertPosition(-1); // Ensure we append to end for main button
            setIsPositionedInsertion(false); // Ensure it's not marked as positioned insertion
            setShowAddTaskDialog(true);

            // Add haptic feedback
            if (window.navigator.vibrate) {
              window.navigator.vibrate(50);
            }
          }}
          sx={{
            position: "fixed",
            top: {
              xs: theme.spacing(15),
              sm: theme.spacing(16),
            },
            right: theme.spacing(3),
            boxShadow: theme.shadows[4],
            zIndex: 1100, // Increased z-index to ensure it's above other elements
            width: 56,
            height: 56,
            transition: "all 0.3s ease",
            "&:hover": {
              transform: "scale(1.1)",
              boxShadow: theme.shadows[8],
            },
            "&:active": {
              transform: "scale(0.95)",
            },
          }}
        >
          <Badge
            color="error"
            badgeContent={tasksArray.length === 0 ? "!" : 0}
            invisible={tasksArray.length > 0}
          >
            <AddIcon fontSize="medium" />
          </Badge>
        </Fab>
      </Tooltip>

      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        PaperProps={{
          sx: {
            borderRadius: "20px",
            background: theme.palette.background.paper,
            padding: 1,
            maxWidth: "400px",
            width: "100%",
          },
        }}
        TransitionComponent={Zoom}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            pb: 1,
          }}
        >
          <Box
            sx={{
              backgroundColor:
                theme.palette.error.lighter || theme.palette.error.light,
              color: theme.palette.error.main,
              width: 40,
              height: 40,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "50%",
            }}
          >
            <DeleteTwoTone />
          </Box>
          <Typography variant="h6">Delete Task?</Typography>
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: theme.palette.text.secondary }}>
            This task will be permanently deleted. This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={() => setOpenDeleteDialog(false)}
            variant="outlined"
            sx={{
              borderRadius: "10px",
              textTransform: "none",
              fontWeight: 600,
              px: 3,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDelete}
            color="error"
            variant="contained"
            sx={{
              borderRadius: "10px",
              textTransform: "none",
              fontWeight: 600,
              px: 3,
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}

TaskTable.propTypes = {
  tasks: PropTypes.array.isRequired,
  setTasks: PropTypes.func.isRequired,
  isAuthor: PropTypes.bool,
  onAdd: PropTypes.func,
};

TaskTable.defaultProps = {
  isAuthor: false,
};
